import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import omit from 'lodash/omit';

import { ReadingFailureReasonUnitPriceModel } from 'models';
import { BaseEditorDialogProps, EditorDialog, LeoDatePicker } from 'components';
import {
  useEditReadingFailureReasonUnitPrice,
  usePutReadingFailureReasonUnitPrice,
} from 'features/readingFailureReasonUnitPrice';
import LeoSelectField from 'components/input-fields/leoSelectField/LeoSelectField';
import LeoTextField from 'components/input-fields/leoTextField/LeoTextField';
import { replaceDecimal } from 'utils/format';

export interface WorkerEditorDialogProps
  extends BaseEditorDialogProps<ReadingFailureReasonUnitPriceModel> {
  wageItemsData: string[];
  wageItemsAccessor: Record<string | number, string>;
}

const TariffsEditorDialog: FC<WorkerEditorDialogProps> = ({
  wageItemsData,
  wageItemsAccessor,
  selected,
  ...props
}) => {
  const { t } = useTranslation();
  const isEdit = Boolean(selected && selected.id);
  const { mutateAsync: createTariffItem } = useEditReadingFailureReasonUnitPrice();
  const { mutateAsync: editTariffItem } = usePutReadingFailureReasonUnitPrice();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const onSubmit = async (values: Partial<ReadingFailureReasonUnitPriceModel>) => {
    const transformedValues = {
      ...values,
      unitPriceForPower: values.unitPriceForPower && replaceDecimal(values.unitPriceForPower),
      unitPriceForGas: values.unitPriceForGas && replaceDecimal(values.unitPriceForGas),
    };

    if (values?.id) {
      await editTariffItem({
        id: values.id,
        data: omit(transformedValues, 'performanceMetric'),
      });
    } else if (values) {
      await createTariffItem({
        data: transformedValues,
      });
    }
  };

  const initVal = isEdit
    ? selected
    : {
        readingFailureReasonId: undefined,
        validFrom: undefined,
        unitPriceForPower: '0',
        unitPriceForGas: '0',
      };

  const dialogTitle = isEdit
    ? `${t('KOZOS_TARIFAK_BERELEMEK.EDIT')} - ${selected?.key} - ${selected?.value}`
    : t('KOZOS_TARIFAK_BERELEMEK.NEW');

  return (
    <EditorDialog
      initialValues={{ ...initVal }}
      maxWidth="sm"
      onSubmit={onSubmit}
      validationSchema={Yup.object().shape({
        readingFailureReasonId: Yup.string().nullable().required(),
        validFrom: Yup.date().nullable().required(),
        unitPriceForPower: Yup.number()
          .required()
          .transform((_, value) => +replaceDecimal(value))
          .min(0)
          .max(99999),
        unitPriceForGas: Yup.number()
          .required()
          .transform((_, value) => +replaceDecimal(value))
          .min(0)
          .max(99999),
      })}
      title={dialogTitle}
      {...props}
    >
      {!isEdit && (
        <LeoSelectField
          name="readingFailureReasonId"
          data={wageItemsData}
          accessor={wageItemsAccessor}
          label="KOZOS_TARIFAK_NEM_LEOLVASHATOSAGI_OK.READING_FAILURE_REASON"
        />
      )}
      <LeoDatePicker
        name="validFrom"
        label={<Trans i18nKey="KOZOS_TARIFAK_BERELEMEK.VALID_FROM" />}
        isYearAndMonthOnly
      />
      <LeoTextField
        name="unitPriceForPower"
        label={<Trans i18nKey="KOZOS_TARIFAK_BERELEMEK.UNIT_PRICE_FOR_POWER" />}
      />
      <LeoTextField
        name="unitPriceForGas"
        label={<Trans i18nKey="KOZOS_TARIFAK_BERELEMEK.UNIT_PRICE_FOR_GAS" />}
      />
    </EditorDialog>
  );
};

export default TariffsEditorDialog;
